<template>
  <div class="esd-page">
    <header class="esd-header">
      <h1 class="esd-title">Timesheets to approve</h1>
      <div v-if="step === 'list'" class="esd-sub" data-test="signed-in-as">
        {{ email }} ·
        <b-button
          variant="link"
          size="sm"
          class="p-0 align-baseline"
          data-test="sign-out"
          @click="signOut()"
        >
          Not you? Sign out
        </b-button>
      </div>
    </header>

    <main class="esd-body">
      <div v-if="step === 'loading'" class="esd-card text-center">
        <b-spinner small /> Loading…
      </div>

      <!-- Step 1: which email the timesheets were sent to -->
      <div v-else-if="step === 'email'" class="esd-card" data-test="email-step">
        <h2 class="esd-h2">Enter your email</h2>
        <p>
          Use the address your timesheet emails come to. We email you a 6-digit
          code to open your timesheets. No account needed.
        </p>
        <b-alert v-if="notice" show variant="info" data-test="notice">
          {{ notice }}
        </b-alert>
        <b-alert v-if="error" show variant="danger" data-test="error">
          {{ error }}
        </b-alert>
        <b-form-input
          v-model.trim="email"
          type="email"
          autocomplete="email"
          placeholder="you@company.com"
          data-test="email-input"
          @keyup.enter="sendCode"
        />
        <b-button
          variant="primary"
          block
          class="mt-3"
          :disabled="busy || !email"
          data-test="send-code-btn"
          @click="sendCode"
        >
          {{ busy ? 'Sending…' : 'Email me a code' }}
        </b-button>
      </div>

      <!-- Step 2: the emailed code -->
      <div v-else-if="step === 'code'" class="esd-card" data-test="code-step">
        <h2 class="esd-h2">Enter your code</h2>
        <p>
          If {{ email }} has timesheets waiting, a code is on its way. It can
          take a minute to arrive.
        </p>
        <b-form-input
          v-model="code"
          inputmode="numeric"
          autocomplete="one-time-code"
          maxlength="12"
          placeholder="6-digit code"
          class="esd-code-input"
          data-test="code-input"
          @keyup.enter="verifyCode"
        />
        <b-alert v-if="error" show variant="danger" class="mt-3 mb-0">
          <span data-test="error">{{ error }}</span>
        </b-alert>
        <b-button
          variant="primary"
          block
          class="mt-3"
          :disabled="busy || !code.trim()"
          data-test="verify-btn"
          @click="verifyCode"
        >
          {{ busy ? 'Checking…' : 'Continue' }}
        </b-button>
        <b-button
          variant="link"
          block
          :disabled="busy"
          data-test="back-to-email"
          @click="backToEmail"
        >
          Use a different email or send a new code
        </b-button>
      </div>

      <!-- Step 3: the list -->
      <template v-else-if="step === 'list'">
        <section class="esd-card esd-filters" data-test="filters">
          <b-form-group label="Show" class="mb-0">
            <b-form-select
              v-model="filters.status"
              :options="statusOptions"
              data-test="filter-status"
            />
          </b-form-group>
          <b-form-group label="Project" class="mb-0">
            <b-form-select
              v-model="filters.projectId"
              :options="projectSelectOptions"
              data-test="filter-project"
            />
          </b-form-group>
          <b-form-group label="Week from" class="mb-0">
            <b-form-input
              v-model="filters.from"
              type="date"
              data-test="filter-from"
            />
          </b-form-group>
          <b-form-group label="Week to" class="mb-0">
            <b-form-input
              v-model="filters.to"
              type="date"
              data-test="filter-to"
            />
          </b-form-group>
          <div class="esd-export">
            <span class="esd-muted">Export these timesheets</span>
            <b-button
              variant="outline-primary"
              size="sm"
              :disabled="exporting || !items.length"
              data-test="export-csv"
              @click="exportList('csv')"
            >
              CSV
            </b-button>
            <b-button
              variant="outline-primary"
              size="sm"
              :disabled="exporting || !items.length"
              data-test="export-pdf"
              @click="exportList('pdf')"
            >
              PDF
            </b-button>
          </div>
        </section>

        <b-alert
          v-if="exportError"
          show
          dismissible
          variant="danger"
          data-test="export-error"
          @dismissed="exportError = null"
        >
          {{ exportError }}
        </b-alert>

        <b-alert
          v-if="result"
          show
          dismissible
          :variant="result.skipped.length ? 'warning' : 'success'"
          data-test="bulk-result"
          @dismissed="result = null"
        >
          <strong>
            {{ result.approved }}
            {{ result.approved === 1 ? 'timesheet' : 'timesheets' }} approved.
          </strong>
          <ul v-if="result.skipped.length" class="mb-0 mt-1 pl-3">
            <li
              v-for="s in result.skipped"
              :key="s.id"
              :data-test="`skipped-${s.id}`"
            >
              {{ s.label }}: {{ s.message }}
            </li>
          </ul>
        </b-alert>

        <b-alert v-if="listError" show variant="danger" data-test="list-error">
          {{ listError }}
          <a href="#" class="alert-link ml-1" @click.prevent="loadList">
            Try again
          </a>
        </b-alert>

        <div v-if="loading" class="esd-card text-center" data-test="loading">
          <b-spinner small /> Loading…
        </div>

        <div
          v-else-if="!items.length && !listError"
          class="esd-card text-center"
          data-test="empty"
        >
          {{
            filtered
              ? 'No timesheets match these filters.'
              : 'You have no timesheets to approve right now.'
          }}
        </div>

        <section v-else-if="items.length" class="esd-card esd-list">
          <div v-if="selectable.length" class="esd-listbar">
            <b-form-checkbox
              :checked="selected.length > 0"
              :indeterminate="
                selected.length > 0 && selected.length < selectable.length
              "
              data-test="select-all"
              @change="toggleAll"
            >
              {{
                selected.length ? `${selected.length} selected` : 'Select all'
              }}
            </b-form-checkbox>
            <span
              v-if="selectable.length > maxBulk"
              class="esd-muted"
              data-test="cap-note"
            >
              {{ selected.length }} of {{ selectable.length }} selected. Approve
              these, then select the rest.
            </span>
          </div>

          <div
            v-for="item in items"
            :key="item.request_id"
            class="esd-row"
            :data-test="`row-${item.request_id}`"
          >
            <b-form-checkbox
              v-if="item.can_bulk_approve"
              :checked="isSelected(item.request_id)"
              :disabled="!isSelected(item.request_id) && atCap"
              :data-test="`tick-${item.request_id}`"
              @change="toggle(item.request_id)"
            />
            <span v-else class="esd-tick-spacer" />
            <button
              type="button"
              class="esd-open"
              :data-test="`open-${item.request_id}`"
              @click="open(item)"
            >
              <span class="esd-who">{{ item.tradie_name }}</span>
              <span class="esd-meta">
                {{ item.project.name }} · {{ weekLabel(item) }}
              </span>
              <span class="esd-meta">
                {{ formatMinutes(item.total_minutes) }}
                <template v-if="!item.decision">
                  · link expires {{ shortDate(item.expires_at) }}
                </template>
              </span>
            </button>
            <b-badge :variant="badgeVariant(item)" class="esd-badge">
              {{ statusLabel(item) }}
            </b-badge>
          </div>
        </section>

        <!-- Approve the ticked sheets exactly as sent -->
        <section v-if="selected.length" class="esd-card" data-test="bulk-panel">
          <h2 class="esd-h2">
            Approve {{ selected.length }}
            {{ selected.length === 1 ? 'timesheet' : 'timesheets' }} as sent
          </h2>
          <p class="esd-muted">
            Only for timesheets you are happy with. To change hours, open the
            timesheet instead.
          </p>
          <b-alert
            v-if="bulkError"
            show
            variant="danger"
            data-test="bulk-error"
          >
            {{ bulkError }}
          </b-alert>
          <b-form-group label="Your full name">
            <b-form-input
              v-model.trim="bulk.name"
              maxlength="255"
              autocomplete="name"
              data-test="bulk-name"
            />
          </b-form-group>
          <b-form-group label="Company (optional)">
            <b-form-input
              v-model.trim="bulk.company"
              maxlength="255"
              data-test="bulk-company"
            />
          </b-form-group>
          <b-form-group label="Position (optional)">
            <b-form-input
              v-model.trim="bulk.position"
              maxlength="255"
              data-test="bulk-position"
            />
          </b-form-group>
          <b-form-checkbox
            v-model="bulk.declaration"
            data-test="bulk-declaration"
          >
            {{ declarationText }}
          </b-form-checkbox>
          <b-button
            variant="primary"
            block
            class="mt-3"
            :disabled="busy || !bulk.name || !bulk.declaration"
            data-test="bulk-approve-btn"
            @click="bulkApprove"
          >
            {{ busy ? 'Approving…' : `Approve ${selected.length} as sent` }}
          </b-button>
        </section>
      </template>
    </main>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import { supervisorDashboardApi } from '@/utils/externalApprovalApi'
import {
  clearDashboardSession,
  clearPendingCode,
  readDashboardSession,
  readPendingCode,
  saveDashboardSession,
  savePendingCode,
} from '@/utils/externalSupervisorSession'
import {
  DECLARATION,
  buildExternalExportRows,
  describeApiError,
  formatMinutes,
} from '@/utils/externalApproval'
import { downloadRowsAsCsv, downloadRowsAsPdf } from '@/utils/pdfExport'

// The API refuses a bulk approve of more than 100 sheets (validation max:100).
const MAX_BULK = 100

const STATUS_OPTIONS = [
  { value: 'pending', text: 'Waiting for me' },
  { value: 'decided', text: 'Finished' },
  { value: 'all', text: 'All' },
]

const DECISION_LABELS = {
  approved: 'Approved',
  amended: 'Approved with changes',
  rejected: 'Rejected',
}

const status = (e) => e && e.response && e.response.status

const PENDING_ID = 'dashboard'

export default {
  name: 'ExternalSupervisorDashboard',

  props: {
    // Injectable for tests; the real client has no account credentials.
    api: { type: Object, default: () => supervisorDashboardApi },
  },

  data() {
    return {
      step: 'email', // loading | email | code | list
      email: '',
      code: '',
      session: null,
      busy: false,
      error: null,
      notice: null,
      items: [],
      // Every project seen so far: the API only returns the projects of the
      // rows that match the current filters, so the options must not shrink.
      projects: [],
      filters: { status: 'pending', projectId: '', from: '', to: '' },
      loading: false,
      listError: null,
      exporting: false,
      exportError: null,
      selected: [],
      bulk: { name: '', company: '', position: '', declaration: false },
      bulkError: null,
      result: null,
      listSeq: 0,
      maxBulk: MAX_BULK,
      statusOptions: STATUS_OPTIONS,
    }
  },

  computed: {
    selectable() {
      return this.items.filter((i) => i.can_bulk_approve)
    },
    atCap() {
      return this.selected.length >= MAX_BULK
    },
    filtered() {
      const f = this.filters
      return f.status !== 'pending' || !!(f.projectId || f.from || f.to)
    },
    projectSelectOptions() {
      return [
        { value: '', text: 'All projects' },
        ...this.projects.map((p) => ({ value: p.id, text: p.name })),
      ]
    },
    declarationText() {
      return DECLARATION(this.bulk.name || '[your name]')
    },
  },

  watch: {
    filters: {
      deep: true,
      handler() {
        if (this.session) this.loadList()
      },
    },
  },

  created() {
    const saved = readDashboardSession()
    if (saved) {
      this.email = saved.email || ''
      this.session = saved.session
      this.step = 'loading'
      this.loadList()
      return
    }
    // A code was already emailed and the tab was reloaded while they read it:
    // back to the code box for that email, not a second code.
    const pending = readPendingCode(PENDING_ID)
    if (pending && pending.sentTo) {
      this.email = pending.sentTo
      this.step = 'code'
    }
  },

  methods: {
    formatMinutes,

    shortDate: (d) => dayjs(d).format('D MMM'),

    weekLabel(item) {
      return `${dayjs(item.week_start).format('D MMM')} – ${dayjs(
        item.week_end,
      ).format('D MMM YYYY')}`
    },

    statusLabel(item) {
      return item.decision
        ? DECISION_LABELS[item.decision] || 'Decided'
        : 'Waiting for you'
    },

    badgeVariant(item) {
      if (!item.decision) return 'warning'
      return item.decision === 'rejected' ? 'danger' : 'success'
    },

    // The API words its own 429s ("Too many codes requested. Please try again
    // in 14 seconds", the code lockout); only the framework's bare "Too Many
    // Attempts." reads badly and is replaced.
    problem(e, fallback) {
      if (status(e) === 429) {
        const message = e.response.data && e.response.data.message
        return message && message !== 'Too Many Attempts.'
          ? message
          : 'Too many attempts. Wait a few minutes and try again.'
      }
      return describeApiError(e, fallback)
    },

    async sendCode() {
      if (!this.email) return
      this.busy = true
      this.error = null
      this.notice = null
      try {
        const res = await this.api.sendCode(this.email)
        savePendingCode(PENDING_ID, this.email, res && res.code_expires_at)
        this.code = ''
        this.step = 'code'
      } catch (e) {
        this.error = this.problem(e, 'We could not send a code. Try again.')
      } finally {
        this.busy = false
      }
    },

    async verifyCode() {
      if (!this.code.trim()) return
      this.busy = true
      this.error = null
      try {
        const res = await this.api.verify(this.email, this.code.trim())
        this.session = res.session
        saveDashboardSession(this.email, res.session, res.session_expires_at)
        clearPendingCode(PENDING_ID)
        this.step = 'loading'
        await this.loadList()
      } catch (e) {
        const left =
          e.response && e.response.data && e.response.data.attempts_remaining
        if (status(e) === 429 || left === 0) {
          clearPendingCode(PENDING_ID)
          this.step = 'email'
          this.notice = null
          this.error = this.problem(
            e,
            'That code no longer works. Ask for a new code.',
          )
          return
        }
        this.error =
          this.problem(e, 'That code is not right.') +
          (left ? ` ${left} attempt${left === 1 ? '' : 's'} left.` : '')
      } finally {
        this.busy = false
      }
    },

    backToEmail() {
      clearPendingCode(PENDING_ID)
      this.error = null
      this.step = 'email'
    },

    // Exports what the filters show, with the hours as the supervisor saw
    // them, like the company supervisor's own export.
    async exportList(format) {
      this.exporting = true
      this.exportError = null
      try {
        const res = await this.api.exportSheets(this.session, this.params())
        const rows = buildExternalExportRows(
          (res.data || []).map((s) => ({
            ...s,
            project_name: s.project && s.project.name,
          })),
        )
        const filename = `Timesheets ${dayjs().format('DD MM YYYY')}`
        if (format === 'pdf') downloadRowsAsPdf(filename, rows)
        else downloadRowsAsCsv(filename, rows)
      } catch (e) {
        if (status(e) === 401) {
          return this.signOut(
            'Your session ended. Enter your email to get a new code.',
          )
        }
        this.exportError = describeApiError(e, 'Could not export. Try again.')
      } finally {
        this.exporting = false
      }
    },

    params() {
      const f = this.filters
      const p = { status: f.status }
      if (f.projectId) p.project_id = f.projectId
      if (f.from) p.from = f.from
      if (f.to) p.to = f.to
      return p
    },

    async loadList() {
      const seq = ++this.listSeq
      this.loading = true
      this.listError = null
      try {
        const res = await this.api.list(this.session, this.params())
        if (seq !== this.listSeq) return
        this.items = res.data || []
        this.email = res.email || this.email
        const known = new Set(this.projects.map((p) => p.id))
        for (const p of res.projects || []) {
          if (!known.has(p.id)) this.projects.push(p)
        }
        const live = new Set(
          this.items.filter((i) => i.can_bulk_approve).map((i) => i.request_id),
        )
        this.selected = this.selected.filter((id) => live.has(id))
        this.step = 'list'
      } catch (e) {
        if (seq !== this.listSeq) return
        if (status(e) === 401) {
          return this.signOut(
            'Your session ended. Enter your email to get a new code.',
          )
        }
        this.listError = describeApiError(e, 'Could not load your timesheets.')
        this.step = 'list'
      } finally {
        if (seq === this.listSeq) this.loading = false
      }
    },

    signOut(message = null) {
      clearDashboardSession()
      this.listSeq += 1 // a list still in flight must not bring the list back
      this.session = null
      this.items = []
      this.selected = []
      this.result = null
      this.loading = false
      this.code = ''
      this.error = null
      this.notice = typeof message === 'string' ? message : null
      this.step = 'email'
    },

    isSelected(id) {
      return this.selected.includes(id)
    },

    toggle(id) {
      this.selected = this.isSelected(id)
        ? this.selected.filter((x) => x !== id)
        : this.selected.length < MAX_BULK
        ? [...this.selected, id]
        : this.selected
    },

    // First 100 in the list's own order (newest first); a bigger backlog is
    // approved in rounds.
    toggleAll() {
      this.selected = this.selected.length
        ? []
        : this.selectable.slice(0, MAX_BULK).map((i) => i.request_id)
    },

    open(item) {
      this.$router.push({
        name: 'external-supervisor-sheet',
        params: { requestId: String(item.request_id) },
      })
    },

    labelFor(id) {
      const item = this.items.find((i) => i.request_id === id)
      return item
        ? `${item.tradie_name}, ${item.project.name}, ${this.weekLabel(item)}`
        : `Timesheet ${id}`
    },

    async bulkApprove() {
      if (this.busy || !this.selected.length) return
      this.busy = true
      this.bulkError = null
      try {
        const res = await this.api.bulkApprove(this.session, {
          request_ids: this.selected,
          supervisor_name: this.bulk.name,
          supervisor_company: this.bulk.company || null,
          supervisor_position: this.bulk.position || null,
          declaration: true,
        })
        this.result = {
          approved: (res.approved || []).length,
          skipped: (res.skipped || []).map((s) => ({
            id: s.id,
            message: s.message,
            label: this.labelFor(s.id),
          })),
        }
        this.selected = []
        this.bulk.declaration = false
        await this.loadList()
        if (typeof window !== 'undefined') window.scrollTo(0, 0)
      } catch (e) {
        if (status(e) === 401) {
          this.signOut(
            'Your session ended. Enter your email to get a new code.',
          )
        } else {
          this.bulkError = describeApiError(
            e,
            'Could not approve these timesheets. Try again.',
          )
        }
      } finally {
        this.busy = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.esd-page {
  min-height: 100vh;
  background: #f5f7fa;
}
.esd-header {
  padding: 16px;
  background: #fff;
  border-bottom: 1px solid #e4e7eb;
}
.esd-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
}
.esd-sub {
  font-size: 0.9rem;
  color: #616e7c;
}
.esd-body {
  max-width: 720px;
  margin: 0 auto;
  padding: 16px;
}
.esd-card {
  background: #fff;
  border: 1px solid #e4e7eb;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
}
.esd-h2 {
  font-size: 1.05rem;
  font-weight: 700;
  margin: 0 0 12px;
}
.esd-code-input {
  font-size: 1.4rem;
  letter-spacing: 0.3em;
  text-align: center;
}
.esd-muted {
  color: #616e7c;
  font-size: 0.85rem;
}
.esd-filters {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}
.esd-export {
  grid-column: 1 / -1;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.esd-listbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e4e7eb;
}
.esd-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #e4e7eb;
  &:last-child {
    border-bottom: 0;
    padding-bottom: 0;
  }
}
.esd-tick-spacer {
  display: inline-block;
  width: 1.5rem;
  flex-shrink: 0;
}
.esd-open {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 0;
  border: 0;
  background: none;
  text-align: left;
  cursor: pointer;
}
.esd-who {
  font-weight: 600;
}
.esd-meta {
  color: #616e7c;
  font-size: 0.85rem;
}
.esd-badge {
  flex-shrink: 0;
}
@media (min-width: 576px) {
  .esd-filters {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}
</style>
